import { ServiceException } from '@inter-ikea-kompis/exceptions';
import { IframeUtility, StringUtility } from '@inter-ikea-kompis/utilities';
import jwtDecode from 'jwt-decode/build/jwt-decode.cjs.js';
import createHash from 'sha.js';
/**
 * This utility is used to handle encoding, decoding, and other operations related to DEXF AccountService tokens.
 */
export default class DexfAccountServiceUtility {
    /**
     * Converts a Buffer into a URL-safe base64 encoded string.
     *
     * @param value The Buffer to convert.
     * @returns The base64 encoded string.
     */
    static base64(value) {
        return value.toString('base64').replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    }
    /**
     * Converts an object's keys from camelCase to snake_case and encodes them into a URL query string.
     *
     * @param data The object to convert.
     * @returns The URL query string.
     */
    static createUrlParameters(data) {
        const snakeData = this.renameKeysToSnake(data);
        return Object.entries(snakeData)
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
            .join('&');
    }
    /**
     * Extracts the 'code' parameter from the current window's URL, if it exists and no error is present.
     *
     * @returns The value of the 'code' parameter, or null if it doesn't exist or an error is present.
     */
    static getCodeSearchParam() {
        try {
            const url = new URL(this.getWindowLocation());
            return this.getCodeFromUrl(url);
        }
        catch (e) {
            throw new ServiceException(`Failed to parse window location. Error: ${e.message}`);
        }
    }
    /**
     * Decodes a JWT token and extracts the displayName property.
     *
     * @param token The JWT token to decode.
     * @returns The displayName.
     * @throws {ServiceException} If decoding the token fails.
     */
    static getIdTokenDisplayName(token) {
        try {
            return jwtDecode(token).displayName;
        }
        catch (error) {
            throw new ServiceException(`Failed to decode JWT token. Error: ${error.message}`);
        }
    }
    /**
     * Generates the initials from a given name.
     *
     * @param name - The full name as a string (e.g., "Dardan Berisha").
     * @returns A string containing the initials (e.g., "DB").
     */
    static getInitials(name) {
        const parts = name.trim().split(/\s+/); // Split by spaces, ignoring extra spaces
        if (parts.length === 1) {
            return parts[0][0].toUpperCase();
        }
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    static createUserFromTokens(tokens) {
        const displayName = DexfAccountServiceUtility.getIdTokenDisplayName(tokens?.idToken);
        const initials = DexfAccountServiceUtility.getInitials(displayName);
        return {
            ...tokens,
            displayName,
            initials
        };
    }
    /**
     * Creates a SHA-256 hash of the given string.
     *
     * @param verifier The string to hash.
     * @returns The SHA-256 hash.
     */
    static createSha256Hash(verifier) {
        return createHash('sha256').update(verifier).digest();
    }
    /**
     * Validates a JWT token's expiry time.
     *
     * @param token The JWT token to validate.
     * @returns True if the token's expiry time is more than 30 seconds in the future, false otherwise.
     */
    static isTokenValid(token) {
        try {
            return (jwtDecode(token).exp >
                this.nowPlus30seconds());
        }
        catch (error) {
            return false;
        }
    }
    /**
     * Check if window is inside an iframe, and getting the parent window location if it is.
     */
    static getWindowLocation() {
        if (IframeUtility.isInsideIframe()) {
            if (!IframeUtility.isParentWindowAccessible()) {
                throw new ServiceException('Failed to redirect to login page in the parent window because it was not accessible. Try to use the usePopup option to login via popup instead.');
            }
            return window.parent.location.href;
        }
        return window.location.href;
    }
    /**
     * Extracts the 'code' parameter from the given URL, if it exists and no error is present.
     *
     * @param url ...
     */
    static getCodeFromUrl(url) {
        return url.searchParams.get('error') ? null : url.searchParams.get('code');
    }
    /**
     * Converts all keys in an object from camelCase to snake_case.
     *
     * @param obj The object whose keys should be converted.
     * @returns The object with converted keys.
     */
    static renameKeysToSnake(obj) {
        const keyValues = Object.keys(obj).map((key) => {
            const newKey = StringUtility.camelToSnake(key);
            return { [newKey]: obj[key] };
        });
        return Object.assign({}, ...keyValues);
    }
    /**
     * Returns a Unix timestamp representing 30 seconds from the current time.
     *
     * @returns The future timestamp.
     */
    static nowPlus30seconds() {
        return Date.now() / 1000 + 30;
    }
}
//# sourceMappingURL=DexfAccountServiceUtility.js.map